import React from 'react';

import { Box, Link, Stack, Typography } from '@mui/material';

type TocItem = { id: string; label: string };

type Props = {
  /** 目次の対象となるスクロールコンテナ（本文側）の ref */
  scrollRef: React.RefObject<HTMLElement | null>;
  /** セクション収集/再計算のトリガ（編集モード切替や detail 変更時に変える） */
  deps?: React.DependencyList;
  /** 目次の見出し（既定: このフォームの内容） */
  title?: string;
  /** 目次の幅（既定 200） */
  width?: number;
};

/**
 * スクロール連動の目次（TOC）サイドパネル。knz リファレンスと同じく、
 * スクロールに追従してアクティブ項目の左に色付きの縦棒が移動する。
 *
 * 対象は `scrollRef` のスクロールコンテナ配下で `data-toc-section` を持つ要素。
 * 各セクション要素に `id` と `data-toc-label`（表示名）を付与しておくこと。
 * 本文の右隣に並べて使う（本文: flex:1 / この目次: 固定幅）。
 */
const FormToc: React.FC<Props> = ({
  scrollRef,
  deps = [],
  title = 'このフォームの内容',
  width = 200,
}) => {
  const [items, setItems] = React.useState<TocItem[]>([]);
  const [activeId, setActiveId] = React.useState<string>('');

  // 本文からセクションを収集
  React.useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const collect = () => {
      const els = Array.from(root.querySelectorAll<HTMLElement>('[data-toc-section]'));
      const next = els
        .filter((el) => el.id)
        .map((el) => ({ id: el.id, label: el.dataset.tocLabel || el.id }));
      setItems((prev) => {
        if (prev.length === next.length && prev.every((p, i) => p.id === next[i].id)) return prev;
        return next;
      });
    };
    collect();
    const observer = new MutationObserver(collect);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // スクロール連動でアクティブ項目を判定
  React.useEffect(() => {
    const root = scrollRef.current;
    if (!root || items.length === 0) return;
    const onScroll = () => {
      const offsetTop = root.getBoundingClientRect().top;
      let current = items[0].id;
      for (const it of items) {
        const el = document.getElementById(it.id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top - offsetTop;
        if (top <= 24) current = it.id;
        else break;
      }
      setActiveId(current);
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => root.removeEventListener('scroll', onScroll);
  }, [items, scrollRef]);

  const scrollTo = (id: string) => {
    const root = scrollRef.current;
    const el = document.getElementById(id);
    if (!root || !el) return;
    const top = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop;
    root.scrollTo({ top: top - 8, behavior: 'smooth' });
  };

  if (items.length === 0) return null;

  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        pl: 2,
        pt: 1,
        overflow: 'auto',
        display: { xs: 'none', md: 'block' },
      }}
    >
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ display: 'block', mb: 1 }}
      >
        {title}
      </Typography>
      <Stack spacing={0}>
        {items.map((it) => (
          <Link
            key={it.id}
            href={`#${it.id}`}
            underline="none"
            onClick={(e) => {
              e.preventDefault();
              scrollTo(it.id);
            }}
            sx={{
              py: 0.5,
              px: 1,
              borderLeft: 2,
              borderColor: activeId === it.id ? 'primary.main' : 'transparent',
              color: activeId === it.id ? 'primary.main' : 'text.secondary',
              fontWeight: activeId === it.id ? 600 : 400,
              fontSize: '0.8rem',
              lineHeight: 1.6,
              transition: 'color 0.15s, border-color 0.15s',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              '&:hover': { color: 'primary.main' },
            }}
          >
            {it.label}
          </Link>
        ))}
      </Stack>
    </Box>
  );
};

export default FormToc;
